/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Content,
  Header,
  Page,
  Progress,
} from '@backstage/core-components';
import {
  Button,
  Chip,
  FormControlLabel,
  Switch,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import RefreshIcon from '@material-ui/icons/Refresh';
import { FleetGraph } from './FleetGraph';
import { FleetOverview } from './FleetOverview';
import { GraphListPanel, groupReposByOrg, repoDisplayName } from './GraphListPanel';
import { RepoGraph } from './RepoGraph';
import { DirOverview } from './DirOverview';
import { CompanyGraph } from './CompanyGraph';
import { KgDoc, shaMatches } from './types';
import { KgRepoMeta, useKnowledgeGraph } from './useKnowledgeGraphApi';
import { KgNodesIcon } from './KgNodesIcon';

/**
 * Knowledge-graph workbench (PLAT-1534). Master-detail: the left panel lists
 * every graph in the artifact store (each row's stats double as the prefetch
 * indicator), with the Global entry preselected. Global renders the real
 * Level-2 fleet graph when the store has one, and a synthesized fleet
 * overview from the cached repo graphs until then. The right side is the
 * GPU-path canvas plus every understanding aid (search, legend toggles,
 * detail panel, table). Read-only.
 */

const useStyles = makeStyles(theme => ({
  workbench: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'stretch',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  main: {
    flex: 1,
    minWidth: 0,
  },
  contextRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  errorPanel: {
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1),
    fontSize: 13,
  },
}));

export const KnowledgeGraphPage = () => {
  const classes = useStyles();
  const [showStubs, setShowStubs] = useState(false);
  // Selection: 'global' or an org/name slug. ?repo=<org/name> deep-links;
  // the URL follows selection so any view is shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<string>(
    searchParams.get('repo')?.trim() || 'global',
  );
  const [repoDoc, setRepoDoc] = useState<KgDoc | null>(null);
  // Giant repos (summary.dirtree) open as a directory overview: no doc
  // is ever fetched for them.
  const [giantSummary, setGiantSummary] = useState<import('./types').KgSummary | null>(null);
  const [repoShown, setRepoShown] = useState<string | null>(null);
  const [repoError, setRepoError] = useState<string | null>(null);
  const [repoLoading, setRepoLoading] = useState(false);

  const {
    data,
    error,
    loading,
    repos,
    listError,
    latestShas,
    latestUpdated,
    healthStatuses,
    entries,
    refresh,
    loadRepoGraph,
    loadSummary,
    loadDirtree,
    loadDirShard,
    loadRepoMeta,
  } = useKnowledgeGraph({ pausePolling: selected !== 'global' });

  // Stored-object provenance for the open repo's toolbar. Garnish: loads
  // once the graph has resolved (repoShown is set only after the doc lands),
  // never gates it, and a slow response for a repo the user already left
  // must not land on the next one.
  const [repoMeta, setRepoMeta] = useState<KgRepoMeta | null>(null);
  useEffect(() => {
    setRepoMeta(null);
    if (!repoShown) return undefined;
    let cancelled = false;
    loadRepoMeta(repoShown).then(m => {
      if (!cancelled) setRepoMeta(m);
    });
    return () => {
      cancelled = true;
    };
  }, [repoShown, loadRepoMeta]);
  // The strip must describe the graph on SCREEN: the page never swaps the
  // doc underfoot ("offer, never swap"), so after a new publish the meta —
  // keyed to the store pointer — would otherwise flip while the canvas
  // still renders the older build.
  const shownMeta = (docSha: string | null | undefined) =>
    repoMeta && docSha && shaMatches(repoMeta.sha, docSha) ? repoMeta : null;
  // One ordering for panel and overview: org groups, alphabetical within.
  const orderedRepos = groupReposByOrg(repos).flatMap(g => g.repos);

  // Monotonic token: any newer selection (repo click, Global, refresh)
  // invalidates in-flight loads, so a slow response can never overwrite a
  // faster later click or resurrect a dismissed repo view.
  const reqSeq = useRef(0);
  const repoShownRef = useRef<string | null>(null);
  repoShownRef.current = repoShown;

  const setRepoParam = (slug: string | null) =>
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('org');
        if (slug) next.set('repo', slug);
        else {
          next.delete('repo');
          next.delete('path');
        }
        return next;
      },
      { replace: true },
    );

  const setOrgParam = (org: string | null) =>
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('repo');
        next.delete('path');
        if (org) next.set('org', org);
        else next.delete('org');
        return next;
      },
      { replace: true },
    );

  // Drill path inside a giant repo — URL-owned so the sidebar tree, the
  // breadcrumb, and deep links all steer the same state.
  const dirPath = searchParams.get('path') ?? '';
  const setPathParam = (path: string) =>
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (path) next.set('path', path);
        else next.delete('path');
        return next;
      },
      { replace: true },
    );

  const showRepo = async (
    name: string,
    opts?: { keepPath?: boolean },
  ): Promise<'ok' | 'failed' | 'superseded'> => {
    const token = ++reqSeq.current;
    if (!opts?.keepPath && name !== repoShownRef.current) setPathParam('');
    setSelected(name);
    setRepoLoading(true);
    setRepoError(null);
    try {
      // No catch-to-null: a summary failure must NOT fall through to a full
      // doc fetch (the repo might be a 518 MB giant) — fail visibly instead.
      const sum = await loadSummary(name);
      if (reqSeq.current !== token) return 'superseded'; // discard quietly
      if (sum?.dirtree) {
        setRepoDoc(null);
        setGiantSummary(sum);
        setRepoShown(name);
        setRepoParam(name);
        return 'ok';
      }
      const doc = await loadRepoGraph(name);
      if (reqSeq.current !== token) return 'superseded'; // discard quietly
      setGiantSummary(null);
      setRepoDoc(doc);
      setRepoShown(name);
      setRepoParam(name);
      return 'ok';
    } catch (e) {
      if (reqSeq.current !== token) return 'superseded';
      // Keep whatever was on screen — a failed open must not destroy the
      // working session. The banner names the failure and what still shows.
      const msg = String((e as Error).message ?? e);
      setRepoError(`Couldn't load "${name}" — ${msg}.`);
      setSelected(repoShownRef.current ?? 'global');
      setRepoParam(repoShownRef.current); // a failed open must not squat in the URL
      return 'failed';
    } finally {
      if (reqSeq.current === token) setRepoLoading(false);
    }
  };

  // Fleet repo rows hand off to the repo's own graph (PLAT-1646). The fleet path no longer
  // applies inside the repo (tail carries an older deep link's sub-path); a failed open
  // returns to the repo's org level rather than the fleet root.
  const openFleetRepo = async (slug: string, tail?: string) => {
    // The tail applies only once the repo is shown: set earlier, the still-mounted Global view flashes "not in this build".
    setPathParam('');
    const outcome = await showRepo(slug, { keepPath: true });
    if (outcome === 'ok') {
      if (tail) setPathParam(tail);
    } else if (outcome === 'failed') setPathParam(slug.split('/')[0]);
  };

  const selectGlobal = () => {
    setGiantSummary(null);
    ++reqSeq.current; // invalidate any in-flight repo load
    setSelected('global');
    setRepoDoc(null);
    setRepoShown(null);
    setRepoError(null);
    setRepoLoading(false);
    setRepoParam(null);
  };

  // Org-scoped fleet view: the same overview, filtered to one org's repos.
  const selectOrg = (org: string) => {
    setGiantSummary(null);
    ++reqSeq.current;
    setRepoDoc(null);
    setRepoShown(null);
    setRepoError(null);
    setRepoLoading(false);
    if (data?.summary && !data.graph) {
      // Giant fleet: the orgs ARE the dirtree's top level — drill instead
      // of switching modes (an org: selection would pause the global poll
      // and render nothing under a summary-only response).
      setSelected('global');
      setOrgParam(null);
      setPathParam(org);
      return;
    }
    setSelected(`org:${org}`);
    setOrgParam(org);
  };


  const onSelect = (name: string) => {
    if (name === selected) return;
    if (name === 'global') selectGlobal();
    else showRepo(name);
  };

  // Deep-link entry: a ?repo= or ?org= arrival loads immediately, once.
  const autoLoaded = useRef(false);
  useEffect(() => {
    const fromUrl = searchParams.get('repo');
    const orgFromUrl = searchParams.get('org');
    if (!autoLoaded.current && fromUrl) {
      autoLoaded.current = true;
      // keepPath: a ?repo=X&path=sub/dir link must open at the linked
      // directory, not the repo root.
      showRepo(fromUrl.trim(), { keepPath: true });
    } else if (!autoLoaded.current && orgFromUrl) {
      autoLoaded.current = true;
      setSelected(`org:${orgFromUrl.trim()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const meta = data?.meta;
  let staleness = 'not loaded yet';
  if (typeof meta?.staleSeconds === 'number') {
    staleness =
      meta.staleSeconds < 90
        ? `loaded ${meta.staleSeconds}s ago`
        : `loaded ${Math.round(meta.staleSeconds / 60)}m ago`;
  }

  const repoMode = selected !== 'global' && repoDoc && repoShown;
  const giantMode =
    selected !== 'global' && !!repoShown && !repoDoc && !!giantSummary;
  const orgMode =
    selected.startsWith('org:') && !repoShown ? selected.slice(4) : null;

  // Deep-link safety net for the same case: a ?org= arrival resolves before
  // /global answers, so convert it to a drill once the summary shape lands.
  useEffect(() => {
    if (orgMode && data?.summary && !data.graph) {
      setSelected('global');
      setOrgParam(null);
      setPathParam(orgMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgMode, data]);
  const overviewOrderedRepos = orgMode
    ? orderedRepos.filter(r => r.startsWith(`${orgMode}/`))
    : orderedRepos;
  // The store's pointer moved past the build on screen: offer, never swap
  // underfoot (layout position and selection are the user's working state).
  let newerSha: string | null = null;
  if (repoMode && repoDoc.built_at_commit) {
    const latest = latestShas[repoShown as string];
    if (latest && !shaMatches(latest, repoDoc.built_at_commit)) newerSha = latest;
  }

  const loadNewest = async () => {
    if (!repoShown) return;
    const token = ++reqSeq.current;
    setRepoLoading(true);
    setRepoError(null);
    try {
      const doc = await loadRepoGraph(repoShown, { force: true });
      if (reqSeq.current !== token) return;
      setRepoDoc(doc);
    } catch (e) {
      if (reqSeq.current !== token) return;
      setRepoError(`Couldn't refresh "${repoShown}" — ${String((e as Error).message ?? e)}.`);
    } finally {
      if (reqSeq.current === token) setRepoLoading(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header
        pageTitleOverride="Knowledge Graph"
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 14 }}>
            <KgNodesIcon variant="solid" style={{ fontSize: 34 }} />
            Knowledge Graph
          </span>
        }
        subtitle={
          repoMode || giantMode
            ? `Symbol graph for ${repoShown} — extracted from the merged trunk, code-only`
            : 'Symbol graphs across the fleet — extracted from merged trunks, joined with the catalog'
        }
      />
      <Content>
        <div className={classes.workbench}>
          <GraphListPanel
            repos={repos}
            listError={listError}
            entries={entries}
            latestShas={latestShas}
            latestUpdated={latestUpdated}
            healthStatuses={healthStatuses}
            selected={repoMode ? (repoShown as string) : selected}
            onSelect={onSelect}
            onSelectOrg={selectOrg}
            loadDirtree={loadDirtree}
            selectedPath={dirPath || undefined}
            onOpenDir={(slug, path) => {
              setPathParam(path);
              if (repoShownRef.current !== slug) void showRepo(slug, { keepPath: true });
            }}
          />
          <div className={classes.main}>
            {repoError && (
              <div role="alert" className={classes.errorPanel}>
                {repoError}
                {repoShown ? ` Still showing ${repoShown}.` : ' Still on the fleet overview.'}
              </div>
            )}
            {(loading || repoLoading) && <Progress />}
            {giantMode && repoShown && giantSummary && (
              <DirOverview
                key={repoShown}
                slug={repoShown}
                summary={giantSummary}
                path={dirPath}
                onNavigate={setPathParam}
                loadDirtree={loadDirtree}
                loadDirShard={loadDirShard}
                meta={shownMeta(giantSummary.built_at_commit)}
                onClose={selectGlobal}
              />
            )}
            {repoMode && (
              <>
                <div className={classes.contextRow}>
                  <Tooltip
                    title={
                      repoDoc.built_at_commit
                        ? `built from commit ${repoDoc.built_at_commit} — click to open it on GitHub`
                        : 'artifact carries no build commit'
                    }
                  >
                    <Chip
                      size="small"
                      color="primary"
                      label={`${repoDisplayName(repoShown as string)}${
                        repoDoc.built_at_commit
                          ? ` @ ${repoDoc.built_at_commit.slice(0, 7)}`
                          : ''
                      }`}
                      // Body-click opens provenance; ONLY the delete icon
                      // returns to Global (a status chip must never be the
                      // page's destructive control). The slug carries the
                      // real org — the store layout is repo/<org>/<name>/.
                      onClick={
                        repoDoc.built_at_commit
                          ? () =>
                              window.open(
                                `https://github.com/${repoShown}/commit/${repoDoc.built_at_commit}`,
                                '_blank',
                                'noopener',
                              )
                          : undefined
                      }
                      onDelete={selectGlobal}
                      deleteIcon={
                        <CancelIcon aria-label={`close ${repoShown} graph, back to Global`} />
                      }
                    />
                  </Tooltip>
                  {newerSha && (
                    <Chip
                      size="small"
                      color="secondary"
                      label={`new build @ ${newerSha.slice(0, 7)} — load newest`}
                      onClick={loadNewest}
                    />
                  )}
                </div>
                <RepoGraph
                  key={repoShown}
                  doc={repoDoc}
                  repoName={repoShown as string}
                  meta={shownMeta(repoDoc.built_at_commit)}
                />
              </>
            )}
            {!repoMode && !giantMode && data && (
              <>
                <div className={classes.contextRow}>
                  {orgMode && data.graph && (
                    <Chip
                      size="small"
                      color="primary"
                      label={`${orgMode} · ${overviewOrderedRepos.length} repo${
                        overviewOrderedRepos.length === 1 ? '' : 's'
                      }`}
                      onDelete={selectGlobal}
                      deleteIcon={
                        <CancelIcon aria-label={`close ${orgMode} view, back to Global`} />
                      }
                    />
                  )}
                  <Tooltip title={`source: ${meta?.source ?? 'unconfigured'}`}>
                    <Chip size="small" label={`global graph: ${staleness}`} />
                  </Tooltip>
                  {meta && (
                    <Chip
                      size="small"
                      label={`${meta.nodeCount} nodes / ${meta.edgeCount} edges in store`}
                    />
                  )}
                  {meta?.lastError && (
                    <Tooltip title={meta.lastError}>
                      <Chip
                        size="small"
                        color="secondary"
                        label="last reload failed — showing last-known-good"
                      />
                    </Tooltip>
                  )}
                  {data.graph && (
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={showStubs}
                          onChange={e => setShowStubs(e.target.checked)}
                        />
                      }
                      label="show auto-discovered stubs"
                    />
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => refresh()}
                  >
                    Refresh now
                  </Button>
                </div>
                {error && (
                  <div role="alert">refresh failed ({error}) — showing last loaded graph</div>
                )}
                {data.graph ? (
                  <FleetGraph doc={data.graph} hideStubs={!showStubs} />
                ) : (
                  data.summary &&
                  // Company altitude first; ?path= drills the fleet tree (org -> repo rows, PLAT-1646), whose repo rows open the repo's own graph.
                  (dirPath === '' ? (
                    <CompanyGraph
                      key={`company-${data.summary.built_at_commit ?? ''}`}
                      loadDirtree={loadDirtree}
                      onOpenRepo={showRepo}
                    />
                  ) : (
                    <DirOverview
                      key={`global-fleet-${data.summary.built_at_commit ?? ''}`}
                      slug="global"
                      summary={data.summary}
                      path={dirPath}
                      onNavigate={setPathParam}
                      loadDirtree={loadDirtree}
                      loadDirShard={loadDirShard}
                      onClose={() => setPathParam('')}
                      onOpenRepo={(slug, tail) => void openFleetRepo(slug, tail)}
                    />
                  ))
                )}
              </>
            )}
            {!repoMode && !giantMode && !data && !loading && orgMode && (
              <div className={classes.contextRow}>
                <Chip
                  size="small"
                  color="primary"
                  label={`${orgMode} · ${overviewOrderedRepos.length} repo${
                    overviewOrderedRepos.length === 1 ? '' : 's'
                  }`}
                  onDelete={selectGlobal}
                  deleteIcon={
                    <CancelIcon aria-label={`close ${orgMode} view, back to Global`} />
                  }
                />
              </div>
            )}
            {!repoMode && !giantMode && !data && !loading && (
              <FleetOverview
                repos={overviewOrderedRepos}
                entries={entries}
                storeStatus={listError ?? error}
                updatedAt={latestUpdated}
                onOpen={showRepo}
              />
            )}
          </div>
        </div>
      </Content>
    </Page>
  );
};
